/**
 * Next Level Training Card Component
 * Displays up to 2 improvement focus items as action phrases.
 * Shows friendly placeholder when no improvements data available.
 *
 * Redesign (2026-06): restyled as an amber "next-level training" card to align with the
 * dashboard redesign. The empty-state copy and improvement text are preserved verbatim
 * so the StudentDashboardPage tests remain valid.
 */

import { Target } from 'lucide-react';

export interface NextLevelTrainingCardProps {
  /** Array of improvement recommendations from latest session report */
  improvements: string[];
}

export function NextLevelTrainingCard({ improvements }: NextLevelTrainingCardProps) {
  const hasImprovements = improvements.length > 0;

  return (
    <div className="flex h-full flex-col rounded-2xl border border-amber-200 bg-amber-50 p-5 shadow-sm">
      <h3 className="mb-3 flex items-center gap-2 font-display text-base font-semibold text-amber-700">
        <span aria-hidden="true">🎯</span> Next Level Training
      </h3>
      {hasImprovements ? (
        <div className="space-y-2">
          {improvements.slice(0, 2).map((improvement, index) => (
            <div key={index} className="flex items-start gap-2">
              <Target className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" aria-hidden="true" />
              <span className="text-sm text-otter-earth-700">{improvement}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="py-2 text-sm text-gray-500">
          Complete a session to see your training focus!
        </p>
      )}
    </div>
  );
}
